import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ScoreGraph from '../../Charts/ScoreGraph';
import ScoreGraphCost from '../../Charts/ScoreGraphCost';
import QuantGraphDetailed from '../../Charts/QuantGraphDetailed';
import QuantGraphGlobal from '../../Charts/QuantGraphGlobal';
import ThemePolarArea from '../../Charts/ThemePolarArea';
import { getDesignBackgroundColors } from '../../../utils/chartPatterns';
import { useTheme } from '@emotion/react';

/**
 * Hidden chart renderer for export image capture
 * Renders charts off-screen and provides refs for image conversion
 */
const ChartRenderer = forwardRef(({ loopId, kpiId }, ref) => {
    const chartRef = useRef(null);
    const styling = useTheme();
    const project = useSelector((state) => state.projects.selectedProject);
    const flatKpis = useSelector((state) => state.themes.flatKpis);
    const flatThemes = useSelector((state) => state.themes.flatThemes);
    
    // Chart sizes
    const chartWidth = 600;
    const chartHeight = 300;

    // Find the loop
    const loop = React.useMemo(() => {
        if (!project || !loopId) return null;
        for (const phase of project.phases || []) {
            const found = (phase.loops || []).find((l) => l.id === loopId);
            if (found) return found;
        }
        return null;
    }, [project, loopId]);

    // Expose the chart ref to parent
    useImperativeHandle(ref, () => ({
        getChartRef: () => chartRef,
    }));

    if (!loop) return null;

    // Get designs
    let designs = [...loop.designs || []].sort((a, b) => a.name.localeCompare(b.name));
    if (loop.reference_design) {
        designs = [loop.reference_design, ...designs];
    }

    // Theme polar area chart (special case - doesn't need designs)
    if (kpiId === 'theme-polar') {
        const loopThemes = loop.loop_themes || [];
        const chartData = {
            projectThemes: loopThemes.map(loopTheme => ({
                theme: loopTheme.theme,
                importance: loopTheme.importance
            }))
        };
        
        return (
            <Box sx={{ width: chartWidth, height: chartHeight }}>
                <ThemePolarArea ref={chartRef} valueData={chartData} maxWidth={false} />
            </Box>
        );
    }

    if (designs.length === 0) return null;

    const loopKpis = (loop.loop_kpis || []).map(lk => ({...lk, kpi_details: flatKpis[lk.kpi]}));
    const loopLayers = loop.loop_layers || [];
    const loopSegments = loop.loop_segments || [];
    let loopThemes = loop.loop_themes || [];
    loopThemes = loopThemes.map(lt => ({
        ...lt, 
        goals: loopKpis.filter(lk => lk.is_selected && lk.kpi_details.theme === lt.theme)
            .flatMap(lk => lk.loop_goals || [])
    }));

    

    // Cost chart
    if (kpiId === 'cost') {
        return (
            <Box sx={{ width: chartWidth, height: chartHeight }}>
                <ScoreGraphCost ref={chartRef} designs={designs} variants={designs.map(d => d.name)} />
            </Box>
        );
    }

    const kpi = flatKpis[kpiId];
    if (!kpi) return null;

    // Qualitative chart
    if (kpi.scale_type === 0) {
        const data = qualitativeDataConstructor(designs, kpiId, loopThemes, flatKpis, flatThemes, styling);
        return (
            <Box sx={{ width: chartWidth, height: chartHeight }}>
                <ScoreGraph
                    ref={chartRef}
                    initData={data}
                    variants={designs.map(d => d.name)}
                    designs={designs}
                    kpi={kpi}
                />
            </Box>
        );
    }

    // Quantitative charts
    if (kpi.scale_type === 1) {
        const data = detailedScoreDataConstructor(designs, kpiId, loopLayers, loopSegments, styling);
        const relevantGoal = loopKpis.find(lk => lk.kpi === kpiId)?.loop_goals?.[0];

        return (
            <Box sx={{ width: chartWidth, height: chartHeight }}>
                <QuantGraphDetailed
                    ref={chartRef}
                    variants={designs.map(d => d.name)}
                    layers={loopLayers}
                    segments={loopSegments}
                    initData={data}
                    segmentFilter={loopSegments.map(s => s.id)}
                    kpi={kpi}
                    goal={relevantGoal}
                    showGoalLine={true}
                />
            </Box>
        );
    }

    if (kpi.scale_type === 2) {
        const data = globalScoreDataConstructor(designs, kpiId, styling);
        const relevantGoal = loopKpis.find(lk => lk.kpi === kpiId)?.loop_goals?.[0];
        
        return (
            <Box sx={{ width: chartWidth, height: chartHeight }}>
                <QuantGraphGlobal
                    ref={chartRef}
                    variants={designs.map(d => d.name)}
                    initData={data}
                    kpi={kpi}
                    goal={relevantGoal}
                />
            </Box>
        );
    }

    return null;
});

ChartRenderer.displayName = 'ChartRenderer';

ChartRenderer.propTypes = {
    loopId: PropTypes.number.isRequired,
    kpiId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

// Helper functions for data construction (copied from GraphSelector.js)
const qualitativeDataConstructor = (designs, kpiId, loopThemes, flatKpis, flatThemes, styling) => {
    let ix = -1;
    return loopThemes.reduce((acc, loopTheme) => {
        loopTheme.goals
            .filter((goal) => flatKpis[goal.kpi].scale_type === 0 && goal.kpi === kpiId)
            .reduce((acc, goal) => {
                let name = flatKpis[goal.kpi].name;
                let value = goal.value;
                if (name.toUpperCase() === "SCORE") {
                    name = `${flatThemes[loopTheme.theme].name}`;
                }

                ix += 1;
                const baseColors = [Object.values(styling.palette.graphics)[ix]];
                const allDesignColors = [baseColors[0], ...getDesignBackgroundColors(designs, [baseColors[0]])];
                
                acc.push({
                    label: name,
                    data: [
                        value,
                        ...designs.map((design) => {
                            const scoreObj = design.scores?.find((score) => score.kpi === goal.kpi);
                            return scoreObj ? scoreObj.value : null;
                        }),
                    ],
                    backgroundColor: allDesignColors,
                });
                return acc;
            }, acc);
        return acc;
    }, []);
};

const detailedScoreDataConstructor = (designs, kpiId, loopLayers, loopSegments, styling) => {
    return loopLayers.map((loopLayer, layerIndex) => {
        const baseColor = Object.values(styling.palette.graphics)[layerIndex];
        return {
            label: loopLayer.layer_details.name,
            data: designs.map((design) => {
                const score = design.scores?.find((score) => score.kpi === kpiId);
                let layersToCheck = [loopLayer.id];

                if (score?.value) {
                    return score.value
                        .filter((segment) => loopSegments.map(s => s.id).includes(segment.segment) || segment.segment === "total")
                        .reduce(
                            (acc, segment) =>
                                acc +
                                Number(
                                    segment.values
                                        .filter((layerScore) => layersToCheck.includes(layerScore.layer))
                                        .reduce((acc, layerScore) => acc + Number(layerScore.value), 0)
                                ),
                            0
                        );
                }
                return 0;
            }),
            type: "bar",
            backgroundColor: getDesignBackgroundColors(designs, [baseColor]),
            legendColor: baseColor,
        };
    });
};

const globalScoreDataConstructor = (designs, kpiId, styling) => {
    const baseColor = Object.values(styling.palette.graphics)[0];
    return {
        label: "",
        data: designs.map((design) => {
            const score = design.scores?.find((score) => score.kpi === kpiId);
            return score ? score.value : null;
        }),
        type: "bar",
        backgroundColor: getDesignBackgroundColors(designs, [baseColor]),
        legendColor: baseColor,
    };
};

export default ChartRenderer;

